// La pill "Tipo de video" de la caja del Creador y su menú
// (ADR-2026-09-18-el-creador-de-video-escribe-el-prompt-con-skills-por-tipo).
//
// La pill dice "General" o el nombre del tipo elegido y abre el menú: "General" primero y
// después una sección por categoría con cards 9:16 (el clip de ejemplo en loop mudo
// cuando existe; el nombre sobre gris mientras no). Elegir cierra. Con un tipo elegido, la
// X de la pill vuelve a General sin abrir el menú.
//
// Desktop: modal centrado en la pantalla con velo oscuro (decisión de producto 2026-09-18:
// antes era un popover anclado a la pill, que quedaba chico y pegado al pie), portaleado
// al body como VisorDeAdjunto para salir del stacking de la columna. Mobile: bottom sheet
// que ocupa casi toda la pantalla. Los dos son el mismo contenido (`Catalogo`).

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronDown, Clapperboard, Sparkles, X } from 'lucide-react';
import { useIsMdUp } from '../asistente/useIsMdUp';
import {
  GENERAL,
  agruparPorCategoria,
  esGeneral,
  useVideoTipos,
  type TipoDeVideo,
} from '../../videoTiposCatalog';

export interface TipoDeVideoPillProps {
  /** Slug elegido; `GENERAL.slug` por default. */
  valor: string;
  onChange: (slug: string) => void;
  /**
   * Dónde vive la pill, que es lo único que cambia entre las dos: el MENÚ es el mismo
   * (ADR-2026-09-22-el-tipo-de-video-tambien-en-la-caja-del-agente).
   *
   *  • `creador` (default) — la caja del Creador de video: alta (h-9), redonda de sólo
   *    ícono en el teléfono, y con la X al lado para volver a General.
   *  • `composer` — la fila de pastillas de la caja del Agente: la forma de esas
   *    pastillas (h-7, ícono + valor + chevron, sin rótulo —
   *    ADR-2026-09-22-el-rotulo-vive-solo-en-el-menu) y el valor SIEMPRE visible, también
   *    en el teléfono, porque ahí "General" es lo único que la pastilla dice. Se vuelve a
   *    General desde el menú, que lo ofrece primero; una X en esa fila sería el único
   *    control con dos botones.
   */
  tema?: 'creador' | 'composer';
}

/** Rótulo de la pill. Un slug que no está en el catálogo (tipo apagado) se lee General. */
export function rotuloDePill(valor: string, tipos: TipoDeVideo[]): string {
  if (esGeneral(valor)) return GENERAL.nombre;
  return tipos.find((t) => t.slug === valor)?.nombre ?? GENERAL.nombre;
}

/** Card 9:16 de un tipo. */
const CardTipo: React.FC<{
  tipo: TipoDeVideo;
  elegido: boolean;
  onPick: () => void;
}> = ({ tipo, elegido, onPick }) => (
  <button
    type="button"
    onClick={onPick}
    className="group flex flex-col items-stretch gap-1.5 text-left"
  >
    <div
      className={`relative aspect-[9/16] w-full overflow-hidden rounded-2xl border-2 transition-colors ${
        elegido ? 'border-brand-primary' : 'border-transparent group-hover:border-black/10'
      }`}
    >
      {tipo.videoUrl ? (
        <video
          src={tipo.videoUrl}
          poster={tipo.posterUrl}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        /* Placeholder hasta que existan los clips de ejemplo (D5 del spec). */
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-100 px-3 text-center">
          <span className="text-[12px] font-medium leading-tight text-[#02062B80]">
            {tipo.nombre}
          </span>
        </div>
      )}
      {elegido && (
        <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-brand-primary text-white">
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        </span>
      )}
    </div>
    <span className="truncate text-[13px] font-medium text-brand-black">{tipo.nombre}</span>
  </button>
);

/**
 * General, con la MISMA forma de card 9:16 que los tipos
 * (ADR-2026-09-24-general-tambien-es-una-card). Era una fila ancha a todo el catálogo: se
 * leía como un encabezado o un aviso, no como la primera de las nueve opciones que son
 * todas lo mismo —una manera de hacer el video—.
 *
 * No tiene clip porque no es un estilo sino la ausencia de uno, así que adentro va lo que
 * la fila ancha decía: el ícono y la explicación de una línea. El rótulo de abajo y la
 * marca de elegido son los de `CardTipo`, a propósito.
 */
const CardGeneral: React.FC<{ elegido: boolean; onPick: () => void }> = ({ elegido, onPick }) => (
  <button
    type="button"
    onClick={onPick}
    className="group flex flex-col items-stretch gap-1.5 text-left"
  >
    <div
      className={`relative aspect-[9/16] w-full overflow-hidden rounded-2xl border-2 transition-colors ${
        elegido ? 'border-brand-primary' : 'border-transparent group-hover:border-black/10'
      }`}
    >
      <div
        className={`absolute inset-0 flex flex-col items-center justify-center gap-2.5 px-3 text-center transition-colors ${
          elegido ? 'bg-[#1644EB14]' : 'bg-neutral-100 group-hover:bg-neutral-200/60'
        }`}
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#02062BB3] shadow-sm">
          <Sparkles className="h-4 w-4" />
        </span>
        <span className="text-[12px] font-medium leading-snug text-[#02062B99]">
          {GENERAL.descripcion}
        </span>
      </div>
      {elegido && (
        <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-brand-primary text-white">
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        </span>
      )}
    </div>
    <span className="truncate text-[13px] font-medium text-brand-black">{GENERAL.nombre}</span>
  </button>
);

/** El rótulo de una sección del catálogo. */
const RotuloDeSeccion: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-[12px] font-semibold uppercase tracking-wide text-[#02062B80]">
    {children}
  </h3>
);

/**
 * El contenido del menú: General + secciones por categoría. Compartido por el modal, el
 * bottom sheet y la pantalla del modo.
 *
 * `disposicion` es lo único que cambia entre los tres:
 *  • `ancha` — grilla de 4 columnas (el modal de 720 px).
 *  • `angosta` — grilla de 2 (el bottom sheet del teléfono).
 *  • `fila` — las secciones UNA AL LADO DE LA OTRA, repartiéndose el ancho disponible
 *    (ADR-2026-09-24-el-catalogo-de-tipos-va-en-una-fila). Es para la pantalla del modo,
 *    donde hay ancho de sobra y lo que faltaba era alto: las nueve cards entran en una
 *    línea y el estado vacío vuelve a entrar en la ventana.
 */
const Catalogo: React.FC<{
  valor: string;
  tipos: TipoDeVideo[];
  cargando: boolean;
  onPick: (slug: string) => void;
  disposicion: 'ancha' | 'angosta' | 'fila';
}> = ({ valor, tipos, cargando, onPick, disposicion }) => {
  const grupos = agruparPorCategoria(tipos);

  if (disposicion === 'fila') {
    // Cada sección pesa lo que tiene de cards (`flex-grow` = cantidad), así que todas las
    // cards terminan del mismo ancho y la fila entera llena el espacio. La base
    // (`96px` por card) es el punto en que deja de entrar: por debajo, la fila se parte en
    // dos en vez de achicar las cards hasta que no se vea nada.
    //
    // El TOPE es lo que salva el caso de borde: mientras el catálogo carga —o si viniera
    // vacío— General es la única sección de la fila y sin tope crece a toda la pantalla,
    // o sea una card 9:16 de 1400 px de alto. Con `maxWidth` por card, la fila se apoya a
    // la izquierda en vez de estirarse.
    const seccion = (n: number) => ({ flex: `${n} 1 ${n * 96}px`, maxWidth: n * 200 });
    return (
      <div className="flex flex-wrap items-start gap-x-6 gap-y-5">
        {/* General, primero y con rótulo propio: en una fila de secciones rotuladas, una
            card suelta sin rótulo se despega hacia arriba. */}
        <section className="flex flex-col gap-2.5" style={seccion(1)}>
          <RotuloDeSeccion>Automático</RotuloDeSeccion>
          <CardGeneral elegido={esGeneral(valor)} onPick={() => onPick(GENERAL.slug)} />
        </section>
        {cargando && tipos.length === 0 && (
          <p className="self-center text-[13px] text-[#02062B99]">Cargando tipos de video…</p>
        )}
        {grupos.map((g) => (
          <section key={g.categoria} className="flex flex-col gap-2.5" style={seccion(g.tipos.length)}>
            <RotuloDeSeccion>{g.categoria}</RotuloDeSeccion>
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: `repeat(${g.tipos.length}, minmax(0, 1fr))` }}
            >
              {g.tipos.map((t) => (
                <CardTipo
                  key={t.slug}
                  tipo={t}
                  elegido={valor === t.slug}
                  onPick={() => onPick(t.slug)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    );
  }

  const grilla = disposicion === 'ancha' ? 'grid-cols-4' : 'grid-cols-2';
  return (
    <div className="flex flex-col gap-5">
      {/* General va en la MISMA grilla que los tipos, en la primera celda: es una opción
          más, no un encabezado (ADR-2026-09-24-general-tambien-es-una-card). Sin rótulo de
          sección arriba, porque no es una categoría. */}
      <div className={`grid gap-3 ${grilla}`}>
        <CardGeneral elegido={esGeneral(valor)} onPick={() => onPick(GENERAL.slug)} />
      </div>
      {cargando && tipos.length === 0 && (
        <p className="text-[13px] text-[#02062B99]">Cargando tipos de video…</p>
      )}
      {grupos.map((g) => (
        <section key={g.categoria} className="flex flex-col gap-2.5">
          <RotuloDeSeccion>{g.categoria}</RotuloDeSeccion>
          <div className={`grid gap-3 ${grilla}`}>
            {g.tipos.map((t) => (
              <CardTipo
                key={t.slug}
                tipo={t}
                elegido={valor === t.slug}
                onPick={() => onPick(t.slug)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

/**
 * El MISMO catálogo, pero suelto: para dibujarlo en la pantalla en vez de adentro del
 * menú (ADR-2026-09-24-los-tipos-de-video-se-ven-en-la-pantalla).
 *
 * Trae los tipos por su cuenta —el hook cachea el catálogo por sesión, así que montarlo
 * al lado de la pill no es una consulta más— y recibe el MISMO `valor` y el MISMO setter
 * que la pill, que es lo que hace que elegir acá se vea allá y al revés.
 */
export const CatalogoDeTipoDeVideo: React.FC<{
  valor: string;
  onChange: (slug: string) => void;
  /** La misma de `Catalogo`. En la pantalla del modo, `fila` (el default acá). */
  disposicion?: 'ancha' | 'angosta' | 'fila';
}> = ({ valor, onChange, disposicion = 'fila' }) => {
  const { tipos, cargando } = useVideoTipos();
  return (
    <Catalogo
      valor={valor}
      tipos={tipos}
      cargando={cargando}
      onPick={onChange}
      disposicion={disposicion}
    />
  );
};

/**
 * El menú del catálogo: modal centrado en escritorio, bottom sheet en el teléfono.
 *
 * Vivía adentro de `TipoDeVideoPill` como una constante. Se sacó acá porque ahora lo abren
 * DOS controles —la pill del tema `creador` y la imagen anclada de la caja del composer
 * (ADR-2026-09-24-el-tipo-elegido-se-ancla-a-la-caja)— y tener el mismo modal escrito dos
 * veces es la forma de que uno de los dos se quede viejo.
 *
 * Se EXPORTA porque el estado activo del Creador lo abre desde su menú "+", que ahí es la
 * única puerta al catálogo cuando el tipo es General y no hay nada anclado
 * (ADR-2026-09-24-en-el-estado-activo-el-catalogo-vive-en-el-mas).
 */
export const MenuDeTipos: React.FC<{
  abierto: boolean;
  onCerrar: () => void;
  valor: string;
  onPick: (slug: string) => void;
}> = ({ abierto, onCerrar, valor, onPick }) => {
  const isMdUp = useIsMdUp();
  const { tipos, cargando } = useVideoTipos();

  useEffect(() => {
    if (!abierto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [abierto, onCerrar]);

  if (!abierto) return null;
  return isMdUp
      ? ReactDOM.createPortal(
          <>
            <div className="fixed inset-0 z-[220] bg-black/40" onClick={() => onCerrar()} />
            {/* Centrado con flex y no con translate: el panel tiene alto variable
                (max-h + scroll) y así el centro se mantiene con cualquier contenido. */}
            <div className="pointer-events-none fixed inset-0 z-[221] flex items-center justify-center p-6">
              <div
                role="dialog"
                aria-label="Tipo de video"
                className="pointer-events-auto flex max-h-[85vh] w-[720px] max-w-full flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
              >
                <div className="flex items-center justify-between px-6 pt-5 pb-3">
                  <h2 className="text-[18px] font-semibold text-brand-black">Tipo de video</h2>
                  <button
                    type="button"
                    aria-label="Cerrar"
                    onClick={() => onCerrar()}
                    className="rounded-full p-1.5 hover:bg-neutral-100"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="min-h-0 overflow-y-auto px-6 pb-6">
                  <Catalogo
                    valor={valor}
                    tipos={tipos}
                    cargando={cargando}
                    onPick={onPick}
                    disposicion="ancha"
                  />
                </div>
              </div>
            </div>
          </>,
          document.body,
        )
      : ReactDOM.createPortal(
          <>
            <div className="fixed inset-0 z-[220] bg-black/40" onClick={() => onCerrar()} />
            <div
              role="dialog"
              aria-label="Tipo de video"
              // Alto FIJO y no un tope: el sheet siempre llega casi arriba de todo, no
              // depende de cuánto contenido haya.
              className="fixed inset-x-0 bottom-0 z-[221] flex h-[92vh] flex-col overflow-hidden rounded-t-3xl bg-white"
            >
              <div className="flex items-center justify-between px-5 pt-5 pb-3">
                <h2 className="text-[16px] font-semibold text-brand-black">Tipo de video</h2>
                <button
                  type="button"
                  aria-label="Cerrar"
                  onClick={() => onCerrar()}
                  className="rounded-full p-1.5 hover:bg-neutral-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="min-h-0 overflow-y-auto px-5 pb-8">
                <Catalogo
                  valor={valor}
                  tipos={tipos}
                  cargando={cargando}
                  onPick={onPick}
                  disposicion="angosta"
                />
              </div>
            </div>
          </>,
          document.body,
        );
};

/**
 * EL TIPO ELEGIDO, ANCLADO A LA CAJA — ADR-2026-09-24-el-tipo-elegido-se-ancla-a-la-caja.
 *
 * Reemplaza a la pill "General" que vivía en la fila de controles del composer. Con el
 * tipo en General **no dibuja nada**: General no es un tipo sino su ausencia, y una pill
 * que dice "General" ocupaba un lugar de la fila para contar que no se eligió nada.
 *
 * Cuando SÍ hay tipo elegido, muestra su propio clip de ejemplo en la fila de adjuntos de
 * la caja, con la misma anatomía que un adjunto (76px, rótulo abajo sobre degradé, X
 * arriba a la derecha): lo que se eligió se parece a lo que va a salir, que es más de lo
 * que decía el nombre solo.
 *
 * Sigue siendo el CONTROL, no una etiqueta: tocarlo reabre el catálogo —en el estado
 * activo del modo es la única puerta, porque ahí la pantalla es la grilla de resultados y
 * el catálogo de abajo no existe— y la X vuelve a General.
 */
export const TipoDeVideoAnclado: React.FC<{
  valor: string;
  onChange: (slug: string) => void;
}> = ({ valor, onChange }) => {
  const { tipos } = useVideoTipos();
  const [abierto, setAbierto] = useState(false);
  const tipo = tipos.find((t) => t.slug === valor);

  const pick = (slug: string) => {
    onChange(slug);
    setAbierto(false);
  };

  // General = sin tipo: no hay nada que anclar. El menú tampoco se monta, así que no queda
  // un portal vivo por una caja que no muestra nada.
  if (esGeneral(valor)) return null;

  return (
    <>
      <div className="group relative h-[76px] w-[76px] shrink-0 overflow-hidden rounded-xl border border-black/[0.06] bg-neutral-100">
        <button
          type="button"
          onClick={() => setAbierto(true)}
          aria-haspopup="dialog"
          aria-label={`Tipo de video: ${tipo?.nombre ?? valor}. Cambiar`}
          className="absolute inset-0 transition-transform active:scale-[0.97]"
        >
          {tipo?.videoUrl ? (
            <video
              src={tipo.videoUrl}
              poster={tipo.posterUrl}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : tipo?.posterUrl ? (
            <img src={tipo.posterUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            /* Sin clip ni poster —o mientras el catálogo carga— el ícono del modo, que es
               lo que la pill mostraba al lado del nombre. */
            <span className="absolute inset-0 flex items-center justify-center text-[#02062B66]">
              <Clapperboard className="h-6 w-6" strokeWidth={2} />
            </span>
          )}
        </button>
        {/* El nombre abajo sobre degradé: la misma pastilla que lleva un adjunto para decir
            qué es (ADR-2026-09-08-los-adjuntos-de-la-caja-dicen-que-son-y-se-abren). */}
        <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-1.5 pt-5 pb-1">
          <span className="block truncate text-center text-[10.5px] font-semibold leading-tight text-white">
            {tipo?.nombre ?? 'Tipo'}
          </span>
        </span>
        {/* La X, hermana del botón y no su hija: un botón dentro de otro es HTML inválido.
            Mobile siempre, escritorio al hover — igual que la de los adjuntos. */}
        <button
          type="button"
          onClick={() => onChange(GENERAL.slug)}
          aria-label="Volver a General"
          className="absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/55 text-white shadow-sm opacity-100 transition-opacity hover:bg-black/75 md:opacity-0 md:group-hover:opacity-100"
        >
          <X className="h-3 w-3" strokeWidth={2.5} />
        </button>
      </div>
      <MenuDeTipos
        abierto={abierto}
        onCerrar={() => setAbierto(false)}
        valor={valor}
        onPick={pick}
      />
    </>
  );
};

export const TipoDeVideoPill: React.FC<TipoDeVideoPillProps> = ({
  valor,
  onChange,
  tema = 'creador',
}) => {
  const { tipos } = useVideoTipos();
  const [abierto, setAbierto] = useState(false);
  const elegido = !esGeneral(valor);
  const rotulo = rotuloDePill(valor, tipos);

  const pick = (slug: string) => {
    onChange(slug);
    setAbierto(false);
  };

  const menu = (
    <MenuDeTipos
      abierto={abierto}
      onCerrar={() => setAbierto(false)}
      valor={valor}
      onPick={pick}
    />
  );

  if (tema === 'composer') {
    return (
      <div className="relative flex shrink-0 items-center">
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          aria-haspopup="true"
          aria-expanded={abierto}
          aria-label={`Tipo de video: ${rotulo}`}
          // La forma EXACTA de las pastillas de la caja (`PastillasDeContenido`): h-7,
          // 12.5px, ícono 3.5 y el chevron al final. Si fuera la de la caja del Creador
          // —una cabeza más alta— se leería como un botón de otra fila.
          className={`inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-full pl-2.5 pr-1.5 text-[12.5px] transition-colors ${
            abierto
              ? 'bg-neutral-200 text-brand-black'
              : 'bg-neutral-100 text-[#02062B]/[0.7] hover:bg-neutral-200'
          }`}
        >
          <Clapperboard className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} aria-hidden="true" />
          <span className="font-semibold text-brand-black">{rotulo}</span>
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 text-[#02062B]/[0.4] transition-transform ${
              abierto ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
        </button>
        {menu}
      </div>
    );
  }

  return (
    <div className="relative flex shrink-0 items-center">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-label={`Tipo de video: ${rotulo}`}
        aria-expanded={abierto}
        // Misma forma que la ruedita y el "Añadir" de al lado: pill con ícono + rótulo en
        // desktop, redonda de sólo ícono en mobile. Con tipo elegido, fondo más marcado y
        // la X para volver a General; en mobile, un punto en vez de la X.
        className={`relative inline-flex h-9 w-9 items-center justify-center gap-1.5 rounded-full transition-colors md:w-auto md:px-3.5 ${
          elegido || abierto
            ? 'bg-[#02062B14] text-[#02062BE6]'
            : 'bg-neutral-100 text-[#02062BB3] hover:bg-neutral-200'
        }`}
      >
        <Clapperboard className="h-4 w-4 shrink-0" strokeWidth={2.25} />
        <span className="hidden whitespace-nowrap text-[13.5px] font-medium md:inline">
          {rotulo}
        </span>
        {elegido && (
          <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-brand-primary md:hidden" />
        )}
      </button>
      {elegido && (
        <button
          type="button"
          aria-label="Volver a General"
          onClick={() => onChange(GENERAL.slug)}
          className="ml-1 hidden h-6 w-6 items-center justify-center rounded-full text-[#02062B99] hover:bg-neutral-200 md:inline-flex"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      )}
      {menu}
    </div>
  );
};
